<template>
  <div ref="pickerRoot" class="patient-picker-standard">
    <label :for="inputId" class="patient-picker-standard__label">{{
      label
    }}</label>
    <div class="patient-picker-standard__field">
      <div
        class="patient-picker-standard__control"
        :class="{ 'is-selected': Boolean(value), 'is-open': isOpen }"
      >
        <span class="patient-picker-standard__icon" aria-hidden="true">
          <i class="fas fa-search"></i>
        </span>
        <input
          :id="inputId"
          ref="input"
          class="patient-picker-standard__input"
          :value="query"
          type="search"
          autocomplete="off"
          :placeholder="placeholder"
          role="combobox"
          :aria-expanded="String(isOpen)"
          :aria-controls="resultsId"
          @input="onInput"
          @focus="onFocus"
          @keydown.enter.prevent="submitSearch"
          @keydown.esc.prevent="close"
        />
        <button
          v-if="value"
          type="button"
          class="patient-picker-standard__clear"
          aria-label="Hapus pasien terpilih"
          @click="clear"
        >
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>
        <button
          v-else
          type="button"
          class="patient-picker-standard__search"
          :disabled="loading"
          @click="submitSearch"
        >
          <i
            v-if="loading"
            class="fas fa-spinner fa-spin"
            aria-hidden="true"
          ></i>
          <span v-else>Lihat pasien</span>
        </button>
      </div>

      <div
        v-if="isOpen"
        :id="resultsId"
        class="patient-picker-standard__results"
        role="listbox"
        aria-label="Hasil pencarian pasien"
      >
        <div v-if="loading" class="patient-picker-standard__message">
          <i class="fas fa-spinner fa-spin"></i> Mencari pasien…
        </div>
        <div
          v-else-if="errorMessage"
          class="patient-picker-standard__message is-error"
        >
          {{ errorMessage }}
        </div>
        <div
          v-else-if="hasSearched && !options.length"
          class="patient-picker-standard__message"
        >
          Pasien tidak ditemukan. Coba nama atau nomor lain.
        </div>
        <div
          v-else-if="!hasSearched && !options.length"
          class="patient-picker-standard__message"
        >
          Ketik minimal {{ minChars }} karakter untuk mencari pasien.
        </div>
        <template v-else>
          <button
            v-for="patient in options"
            :key="patient.id"
            type="button"
            class="patient-picker-standard__option"
            role="option"
            @click="select(patient)"
          >
            <span
              ><strong>{{ patient.name }}</strong
              ><small>{{ patientNumber(patient) }}</small></span
            >
            <b>Pilih <i class="fas fa-arrow-right" aria-hidden="true"></i></b>
          </button>
        </template>
      </div>
    </div>

    <div v-if="value" class="patient-picker-standard__selected">
      <i class="fas fa-check-circle" aria-hidden="true"></i>
      <span
        ><small>Pasien terpilih</small><strong>{{ value.name }}</strong></span
      >
      <em>{{ patientNumber(value) }}</em>
    </div>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service.js";

let pickerSequence = 0;

export default {
  name: "PatientPicker",
  props: {
    value: { type: Object, default: null },
    label: { type: String, default: "Pasien" },
    placeholder: { type: String, default: "Cari nama atau nomor rekam medis" },
    minChars: { type: Number, default: 2 },
    limit: { type: Number, default: 8 },
  },
  data() {
    pickerSequence += 1;
    return {
      uid: pickerSequence,
      query: this.value && this.value.name ? this.value.name : "",
      options: [],
      loading: false,
      hasSearched: false,
      isOpen: false,
      errorMessage: "",
      timer: null,
      requestId: 0,
    };
  },
  computed: {
    inputId() {
      return `patient-picker-${this.uid}`;
    },
    resultsId() {
      return `patient-picker-results-${this.uid}`;
    },
    canSearch() {
      return this.query.trim().length >= this.minChars;
    },
  },
  watch: {
    value(patient, previous) {
      if (patient && patient.name) this.query = patient.name;
      else if (!patient && previous && this.query === previous.name)
        this.query = "";
    },
  },
  mounted() {
    document.addEventListener("pointerdown", this.onPointerDownOutside);
  },
  beforeDestroy() {
    document.removeEventListener("pointerdown", this.onPointerDownOutside);
    clearTimeout(this.timer);
    this.requestId += 1;
  },
  methods: {
    onPointerDownOutside(event) {
      const root = this.$refs.pickerRoot;
      if (this.isOpen && root && !root.contains(event.target)) this.close();
    },
    submitSearch() {
      return this.canSearch ? this.searchNow() : this.loadRecent();
    },
    focus() {
      this.open();
    },
    async open() {
      if (!this.$refs.input) return;
      this.$refs.input.focus();
      this.$refs.input.scrollIntoView({ behavior: "smooth", block: "center" });
      this.isOpen = true;
      if (!this.query.trim() && !this.options.length && !this.loading)
        await this.loadRecent();
    },
    onFocus() {
      this.isOpen = true;
      if (!this.query.trim() && !this.options.length && !this.loading)
        this.loadRecent();
    },
    onInput(event) {
      const next = event.target.value;
      this.query = next;
      this.errorMessage = "";
      this.hasSearched = false;
      if (this.value && next !== this.value.name) this.$emit("clear");
      clearTimeout(this.timer);
      if (!this.canSearch) {
        this.options = [];
        this.isOpen = false;
        return;
      }
      this.timer = setTimeout(this.searchNow, 300);
    },
    async searchNow() {
      clearTimeout(this.timer);
      if (!this.canSearch) return;
      const currentRequest = ++this.requestId;
      this.loading = true;
      this.hasSearched = true;
      this.errorMessage = "";
      this.isOpen = true;
      try {
        const response = await ApiService.query("patients", {
          params: {
            name: this.query.trim(),
            page_size: this.limit,
            start_date: "",
            end_date: "",
            birt_date: "",
          },
        });
        if (currentRequest !== this.requestId) return;
        this.options = this.items(response.data);
      } catch (error) {
        if (currentRequest !== this.requestId) return;
        this.options = [];
        this.errorMessage = "Pencarian pasien gagal. Silakan coba lagi.";
      } finally {
        if (currentRequest === this.requestId) this.loading = false;
      }
    },
    async loadRecent() {
      const currentRequest = ++this.requestId;
      this.loading = true;
      this.errorMessage = "";
      this.isOpen = true;
      try {
        const response = await ApiService.query("patients", {
          params: { page_size: this.limit },
        });
        if (currentRequest !== this.requestId) return;
        this.options = this.items(response.data);
      } catch (error) {
        if (currentRequest !== this.requestId) return;
        this.options = [];
        this.errorMessage =
          "Daftar pasien belum dapat dimuat. Silakan coba lagi.";
      } finally {
        if (currentRequest === this.requestId) this.loading = false;
      }
    },
    items(payload) {
      if (Array.isArray(payload)) return payload;
      if (payload && Array.isArray(payload.data)) return payload.data;
      if (payload && payload.data && Array.isArray(payload.data.data))
        return payload.data.data;
      if (payload && Array.isArray(payload.items)) return payload.items;
      return [];
    },
    patientNumber(patient) {
      return (
        patient.local_medical_record_number ||
        patient.patient_id ||
        patient.patient_number ||
        patient.medical_record_number ||
        patient.id_card_number ||
        `ID ${patient.id}`
      );
    },
    select(patient) {
      this.query = patient.name;
      this.options = [];
      this.isOpen = false;
      this.hasSearched = false;
      this.$emit("select", patient);
    },
    clear() {
      clearTimeout(this.timer);
      this.requestId += 1;
      this.query = "";
      this.options = [];
      this.isOpen = false;
      this.hasSearched = false;
      this.errorMessage = "";
      this.$emit("clear");
      this.$nextTick(this.focus);
    },
    close() {
      this.isOpen = false;
    },
  },
};
</script>

<style scoped>
.patient-picker-standard {
  position: relative;
  width: 100%;
  max-width: 100%;
  color: #183153;
}
.patient-picker-standard__label {
  display: block;
  margin: 0 0 0.4rem;
  color: #52677a;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.02em;
}
.patient-picker-standard__field {
  position: relative;
}
.patient-picker-standard__control {
  display: flex;
  align-items: center;
  width: 100%;
  height: 46px;
  min-height: 46px;
  overflow: hidden;
  padding: 0;
  border: 1px solid #cbdbe6;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(30, 64, 92, 0.06);
  transition: border-color 0.15s, box-shadow 0.15s;
}
.patient-picker-standard__control:focus-within,
.patient-picker-standard__control.is-open {
  border-color: #0f766e;
  box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.14);
}
.patient-picker-standard__icon {
  display: grid;
  place-items: center;
  flex: 0 0 44px;
  align-self: stretch;
  color: #0f766e;
  background: #f0fdfa;
}
.patient-picker-standard__icon i {
  color: #0f766e;
}
.patient-picker-standard .patient-picker-standard__control .patient-picker-standard__input {
  width: auto;
  height: 100%;
  min-height: 0;
  min-width: 0;
  flex: 1;
  margin: 0;
  padding: 0 0.75rem;
  border: 0 !important;
  border-radius: 0 !important;
  outline: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  color: #183153;
  font-size: 0.82rem;
  line-height: normal;
  appearance: none;
  -webkit-appearance: none;
}
.patient-picker-standard__input::-webkit-search-cancel-button,
.patient-picker-standard__input::-webkit-search-decoration {
  display: none;
  -webkit-appearance: none;
}
.patient-picker-standard__input::placeholder {
  color: #8496a8;
}
.patient-picker-standard .patient-picker-standard__search,
.patient-picker-standard .patient-picker-standard__clear {
  align-self: stretch;
  display: grid;
  place-items: center;
  height: 100%;
  min-width: 66px;
  margin: 0;
  padding: 0 0.85rem;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  font-size: 0.72rem;
  font-weight: 800;
}
.patient-picker-standard .patient-picker-standard__search {
  background: linear-gradient(135deg, #0f6c9b, #0f766e) !important;
  color: #fff !important;
}
.patient-picker-standard .patient-picker-standard__search:disabled {
  cursor: not-allowed;
  background: #d9e3ea !important;
  color: #7b8b99 !important;
}
.patient-picker-standard .patient-picker-standard__clear {
  min-width: 44px;
  background: #f1f5f9 !important;
  color: #64748b !important;
}
.patient-picker-standard__results {
  position: absolute;
  top: calc(100% + 7px);
  left: 0;
  right: 0;
  z-index: 1055;
  overflow: hidden;
  border: 1px solid #d9e5ed;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 18px 40px rgba(30, 64, 92, 0.2);
}
.patient-picker-standard__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  padding: 0.78rem 0.9rem;
  border: 0;
  border-bottom: 1px solid #edf2f5;
  background: #fff;
  color: #183153;
  text-align: left;
}
.patient-picker-standard__option:last-child {
  border-bottom: 0;
}
.patient-picker-standard__option:hover,
.patient-picker-standard__option:focus-visible {
  outline: 0;
  background: #f0fdfa;
}
.patient-picker-standard__option span,
.patient-picker-standard__option strong,
.patient-picker-standard__option small {
  display: block;
}
.patient-picker-standard__option strong {
  font-size: 0.8rem;
}
.patient-picker-standard__option small {
  margin-top: 0.2rem;
  color: #64748b;
  font-size: 0.68rem;
}
.patient-picker-standard__option b {
  flex: 0 0 auto;
  color: #0f766e;
  font-size: 0.7rem;
}
.patient-picker-standard__option b i {
  margin-left: 0.25rem;
}
.patient-picker-standard__message {
  padding: 1rem;
  color: #64748b;
  font-size: 0.76rem;
  text-align: center;
}
.patient-picker-standard__message.is-error {
  color: #b42318;
  background: #fff5f5;
}
.patient-picker-standard__selected {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-top: 0.45rem;
  padding: 0.48rem 0.65rem;
  border: 1px solid #b9e5dc;
  border-radius: 10px;
  background: #f0fdfa;
}
.patient-picker-standard__selected > i {
  color: #0f766e;
}
.patient-picker-standard__selected span {
  min-width: 0;
  flex: 1;
}
.patient-picker-standard__selected small,
.patient-picker-standard__selected strong {
  display: block;
}
.patient-picker-standard__selected small {
  color: #64748b;
  font-size: 0.6rem;
}
.patient-picker-standard__selected strong {
  overflow: hidden;
  font-size: 0.72rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.patient-picker-standard__selected em {
  color: #52677a;
  font-size: 0.64rem;
  font-style: normal;
}
@media (max-width: 520px) {
  .patient-picker-standard {
    width: 100%;
  }
  .patient-picker-standard__option {
    align-items: flex-start;
  }
  .patient-picker-standard__selected em {
    display: none;
  }
}
</style>
